import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
} from '@mui/material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Add as AddIcon } from '@mui/icons-material';
import AppointmentService from '../../services/AppointmentService.js';
import HolidayService from '../../services/HolidayService.js';
import './AppointmentCalendar.css'; // Import custom CSS for calendar styling

// Setup the localizer for react-big-calendar using date-fns
const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const AppointmentCalendar = () => {
  const navigate = useNavigate();
  
  const [appointments, setAppointments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  
  // Create the ref at the top level of the component
  const prevDate = React.useRef({ year: new Date().getFullYear(), month: new Date().getMonth() });
  
  useEffect(() => {
    fetchAppointmentsAndHolidays();
  }, []);

  const isHolidayDate = (targetDate) => {
    if (!targetDate) return false;
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);

    return holidays.some((holiday) => {
      const startRaw = holiday.date || holiday.Date;
      if (!startRaw) return false;

      const start = new Date(startRaw);
      start.setHours(0, 0, 0, 0);

      if (holiday.isRecurringYearly) {
        return start.getMonth() === target.getMonth() && start.getDate() === target.getDate();
      }

      const endRaw = holiday.endDate || holiday.EndDate;
      const end = endRaw ? new Date(endRaw) : new Date(start);
      end.setHours(0, 0, 0, 0);

      return start <= target && target <= end;
    });
  };
  
  // Refetch data when the date changes significantly (e.g., navigating to a different year)
  useEffect(() => {
    // Only refetch if the year or month changes significantly
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    
    // Check if we've moved far enough to warrant a refetch
    if (Math.abs(currentYear - prevDate.current.year) >= 1 || 
        Math.abs(currentMonth - prevDate.current.month) >= 2) {
      fetchAppointmentsAndHolidays();
      prevDate.current = { year: currentYear, month: currentMonth };
    } else {
      // Even if we don't need to refetch from API, we should update the calendar events
      // to ensure recurring yearly holidays are displayed correctly for the current view year
      updateCalendarEvents();
    }
  }, [date]);
  
  // Function to update calendar events without fetching from API
  const updateCalendarEvents = () => {
    // Transform holidays to calendar events with current view year
    const holidayMap = new Map();
    
    holidays.forEach(holiday => {
      // Parse holiday date
      const holidayDate = new Date(holiday.date);
      
      // For recurring yearly holidays, adjust to current view year
      if (holiday.isRecurringYearly) {
        // Get the current year being viewed in the calendar
        const viewYear = date.getFullYear();
        
        // Create a date for this holiday in the current view year
        const adjustedDate = new Date(holidayDate);
        adjustedDate.setFullYear(viewYear);
        
        // Create a unique key using adjusted date and name
        const key = `${adjustedDate.toISOString().split('T')[0]}-${holiday.name}`;
        
        if (!holidayMap.has(key)) {
          holidayMap.set(key, {
            id: `holiday-${holiday.holidayId}-${viewYear}`,
            title: `🎉 ${holiday.name} (Yearly)`,
            allDay: true,
            start: adjustedDate,
            end: addDays(adjustedDate, 1),
            resource: {
              ...holiday,
              type: 'holiday',
              tooltip: `🎉 ${holiday.name} (Yearly)\nDate: ${format(adjustedDate, 'yyyy-MM-dd')}`
            },
            backgroundColor: '#8a2be2', // Purple for holidays
            borderColor: '#4b0082',
            isHoliday: true
          });
        }
      } else {
        // For non-recurring holidays, handle normally
        const holidayEndDateRaw = holiday.endDate || holiday.EndDate;
        const holidayEndDate = holidayEndDateRaw ? new Date(holidayEndDateRaw) : new Date(holidayDate);
        const holidayEndExclusive = addDays(holidayEndDate, 1);

        // Create a unique key using date and name
        const key = `${holidayDate.toISOString().split('T')[0]}-${holidayEndDate.toISOString().split('T')[0]}-${holiday.name}`;
        
        // Only add this holiday if we haven't seen it before
        if (!holidayMap.has(key)) {
          const startLabel = format(holidayDate, 'yyyy-MM-dd');
          const endLabel = format(holidayEndDate, 'yyyy-MM-dd');
          const dateLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

          holidayMap.set(key, {
            id: `holiday-${holiday.holidayId}`,
            title: `🎉 ${holiday.name}`,
            allDay: true,
            start: holidayDate,
            end: holidayEndExclusive,
            resource: {
              ...holiday,
              type: 'holiday',
              tooltip: `🎉 ${holiday.name}\nDate: ${dateLabel}`
            },
            backgroundColor: '#8a2be2', // Purple for holidays
            borderColor: '#4b0082',
            isHoliday: true
          });
        }
      }
    });
    
    // Convert map values to array
    const holidayEvents = Array.from(holidayMap.values());
    
    // Transform appointments to calendar events (reuse existing logic)
    const appointmentEvents = appointments.map(appointment => {
      // Parse date and time with null checks
      // Backend returns 'appointmentDate' and 'startTime' but frontend expects 'date' and 'time'
      const dateValue = appointment.appointmentDate || appointment.date;
      const timeValue = appointment.startTime || appointment.time;
      
      if (!dateValue || !timeValue) {
        console.warn('Appointment missing date or time:', appointment);
        console.warn('dateValue:', dateValue, 'timeValue:', timeValue);
        return null;
      }
      
      // Handle both ISO date format (YYYY-MM-DDTHH:MM:SS) and simple date format (YYYY-MM-DD)
      let parsedDate;
      if (dateValue.includes('T')) {
        // ISO format: extract date part
        parsedDate = dateValue.split('T')[0];
      } else {
        // Simple format
        parsedDate = dateValue;
      }
      
      // Validate date format (YYYY-MM-DD)
      if (!parsedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        console.warn('Invalid date format:', parsedDate, 'for appointment:', appointment);
        return null;
      }
      
      // Validate time format (HH:MM)
      if (!timeValue.match(/^\d{1,2}:\d{2}$/)) {
        console.warn('Invalid time format:', timeValue, 'for appointment:', appointment);
        return null;
      }
      
      const [year, month, day] = parsedDate.split('-').map(Number);
      const [hours, minutes] = timeValue.split(':').map(Number);
      
      // Create start date
      const start = new Date(year, month - 1, day, hours, minutes);
      
      // Create end date (start + duration)
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + appointment.duration);
      
      // Determine event color based on status
      let backgroundColor = '#3174ad'; // Default blue
      if (appointment.status === 'Completed') {
        backgroundColor = '#5cb85c'; // Green
      } else if (appointment.status === 'Cancelled') {
        backgroundColor = '#d9534f'; // Red
      }
      
      // Create title with patient-physician on first line and time range on second line
      const startTime = appointment.startTime || '';
      const endTime = new Date(start.getTime() + appointment.duration * 60000).toTimeString().slice(0, 5);
      let title = `${appointment.patientNickname || appointment.patientName}${appointment.doctorName ? ` - ${appointment.doctorName}` : ''}\n${startTime} - ${endTime}`;
      if (appointment.isRecurring) {
        title = `↻ ${title}`; // Add recurring symbol
      }
      
      return {
        id: appointment.id,
        title: title,
        start,
        end,
        resource: appointment,
        backgroundColor,
        borderColor: appointment.isRecurring ? '#ff8c00' : undefined, // Orange border for recurring
        borderStyle: appointment.isRecurring ? 'dashed' : undefined
      };
    });

    // Filter out null appointments and combine with holiday events
    const validAppointmentEvents = appointmentEvents.filter(event => event !== null);
    setCalendarEvents([...validAppointmentEvents, ...holidayEvents]);
  };
  
  const fetchAppointmentsAndHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current date and calculate date range for fetching appointments
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      
      // Format dates for API
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      // Fetch appointments from API
      const appointmentsPromise = AppointmentService.getAppointmentsByDateRange(
        formattedStartDate,
        formattedEndDate
      );
      
      // For holidays, use an extended date range to include next year's recurring holidays
      // This ensures yearly holidays appear when navigating to next year in the calendar
      const holidayStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const holidayEndDate = new Date(today.getFullYear() + 1, today.getMonth() + 2, 0);
      
      // Format holiday dates for API
      const formattedHolidayStartDate = holidayStartDate.toISOString().split('T')[0];
      const formattedHolidayEndDate = holidayEndDate.toISOString().split('T')[0];
      
      // Fetch holidays from API with extended date range
      const holidaysPromise = HolidayService.getHolidaysByDateRange(
        formattedHolidayStartDate,
        formattedHolidayEndDate
      );
      
      // Wait for both requests to complete
      const [appointmentsResponse, holidaysResponse] = await Promise.all([
        appointmentsPromise,
        holidaysPromise
      ]);
      
      setAppointments(appointmentsResponse.data);
      setHolidays(holidaysResponse.data);
      
      console.log('Appointments received:', appointmentsResponse.data);
      console.log('Holidays received:', holidaysResponse.data);
      
      // Transform appointments to calendar events
      const appointmentEvents = appointmentsResponse.data.map(appointment => {
        // Parse date and time with null checks
        // Backend returns 'appointmentDate' and 'startTime' but frontend expects 'date' and 'time'
        const dateValue = appointment.appointmentDate || appointment.date;
        const timeValue = appointment.startTime || appointment.time;
        
        if (!dateValue || !timeValue) {
          console.warn('Appointment missing date or time:', appointment);
          console.warn('dateValue:', dateValue, 'timeValue:', timeValue);
          return null;
        }
        
        // Handle both ISO date format (YYYY-MM-DDTHH:MM:SS) and simple date format (YYYY-MM-DD)
        let parsedDate;
        if (dateValue.includes('T')) {
          // ISO format: extract date part
          parsedDate = dateValue.split('T')[0];
        } else {
          // Simple format
          parsedDate = dateValue;
        }
        
        // Validate date format (YYYY-MM-DD)
        if (!parsedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          console.warn('Invalid date format:', parsedDate, 'for appointment:', appointment);
          return null;
        }
        
        // Validate time format (HH:MM)
        if (!timeValue.match(/^\d{1,2}:\d{2}$/)) {
          console.warn('Invalid time format:', timeValue, 'for appointment:', appointment);
          return null;
        }
        
        const [year, month, day] = parsedDate.split('-').map(Number);
        const [hours, minutes] = timeValue.split(':').map(Number);
        
        // Create start date
        const start = new Date(year, month - 1, day, hours, minutes);
        
        // Create end date (start + duration)
        const end = new Date(start);
        end.setMinutes(end.getMinutes() + appointment.duration);
        
        // Determine event color based on status
        let backgroundColor = '#3174ad'; // Default blue
        if (appointment.status === 'Completed') {
          backgroundColor = '#5cb85c'; // Green
        } else if (appointment.status === 'Cancelled') {
          backgroundColor = '#d9534f'; // Red
        }
        
        // Format time for display
        const startTime = timeValue;
        const endTime = end.toTimeString().slice(0, 5);
        
        // Create title with patient-physician on first line and time range on second line
        let title = `${appointment.patientNickname || appointment.patientName}${appointment.doctorName ? ` - ${appointment.doctorName}` : ''}\n${startTime} - ${endTime}`;
        if (appointment.isRecurring) {
          title = `↻ ${title}`; // Add recurring symbol
        }
        
        console.log('Calendar event title created:', title); // Debug log
        
        // Create tooltip with full details including time
        const tooltip = `${title}\nTime: ${startTime} - ${endTime}\nPersons: ${appointment.numberOfPersons || 1}\nStatus: ${appointment.status || 'Scheduled'}`;
        
        return {
          id: appointment.id,
          title: title,
          start,
          end,
          resource: {
            ...appointment,
            tooltip: tooltip,
            startTime: startTime,
            endTime: endTime
          },
          backgroundColor,
          borderColor: appointment.isRecurring ? '#ff8c00' : undefined, // Orange border for recurring
          borderStyle: appointment.isRecurring ? 'dashed' : undefined
        };
      });
      
      // Transform holidays to calendar events
      // First, create a map to track holidays by date+name to prevent duplicates
      const holidayMap = new Map();
      
      holidaysResponse.data.forEach(holiday => {
        // Parse holiday date
        const holidayDate = new Date(holiday.date);
        
        // For recurring yearly holidays, we need to handle them specially
        if (holiday.isRecurringYearly) {
          // Get the current year being viewed in the calendar
          const viewYear = date.getFullYear();
          
          // Create a date for this holiday in the current view year
          const adjustedDate = new Date(holidayDate);
          adjustedDate.setFullYear(viewYear);
          
          // Create a unique key using adjusted date and name
          const key = `${adjustedDate.toISOString().split('T')[0]}-${holiday.name}`;
          
          if (!holidayMap.has(key)) {
            holidayMap.set(key, {
              id: `holiday-${holiday.holidayId}-${viewYear}`,
              title: `🎉 ${holiday.name} (Yearly)`,
              allDay: true,
              start: adjustedDate,
              end: addDays(adjustedDate, 1),
              resource: {
                ...holiday,
                type: 'holiday',
                tooltip: `🎉 ${holiday.name} (Yearly)\nDate: ${format(adjustedDate, 'yyyy-MM-dd')}`
              },
              backgroundColor: '#8a2be2', // Purple for holidays
              borderColor: '#4b0082',
              isHoliday: true
            });
          }
        } else {
          // For non-recurring holidays, handle normally
          const holidayEndDateRaw = holiday.endDate || holiday.EndDate;
          const holidayEndDate = holidayEndDateRaw ? new Date(holidayEndDateRaw) : new Date(holidayDate);
          const holidayEndExclusive = addDays(holidayEndDate, 1);

          // Create a unique key using date and name
          const key = `${holidayDate.toISOString().split('T')[0]}-${holidayEndDate.toISOString().split('T')[0]}-${holiday.name}`;
          
          // Only add this holiday if we haven't seen it before
          if (!holidayMap.has(key)) {
            const startLabel = format(holidayDate, 'yyyy-MM-dd');
            const endLabel = format(holidayEndDate, 'yyyy-MM-dd');
            const dateLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

            holidayMap.set(key, {
              id: `holiday-${holiday.holidayId}`,
              title: `🎉 ${holiday.name}`,
              allDay: true,
              start: holidayDate,
              end: holidayEndExclusive,
              resource: {
                ...holiday,
                type: 'holiday',
                tooltip: `🎉 ${holiday.name}\nDate: ${dateLabel}`
              },
              backgroundColor: '#8a2be2', // Purple for holidays
              borderColor: '#4b0082',
              isHoliday: true
            });
          }
        }
      });
      
      // Convert map values to array
      const holidayEvents = Array.from(holidayMap.values());
      
      // Filter out null appointments and combine with holiday events
      const validAppointmentEvents = appointmentEvents.filter(event => event !== null);
      
      console.log('Total appointment events created:', appointmentEvents.length);
      console.log('Valid appointment events after filtering:', validAppointmentEvents.length);
      console.log('Holiday events:', holidayEvents.length);
      console.log('Final calendar events:', [...validAppointmentEvents, ...holidayEvents]);
      
      setCalendarEvents([...validAppointmentEvents, ...holidayEvents]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setError('Failed to load calendar data. Please try again later.');
      setLoading(false);
    }
  };
  
  const handleSelectEvent = (event) => {
    // Only navigate to appointment details for actual appointments, not holidays
    if (!event.isHoliday) {
      navigate(`/appointments/${event.id}`);
    }
  };
  
  const handleSelectSlot = ({ start }) => {
    if (isHolidayDate(start)) {
      alert('This date is a holiday. Please choose another date.');
      return;
    }
    // Navigate to new appointment form with the selected date/time
    const newAppointmentDate = new Date(start);
    navigate('/appointments/new', { state: { selectedDate: newAppointmentDate } });
  };
  
  const handleViewChange = (newView) => {
    setView(newView);
  };
  
  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const components = React.useMemo(() => ({
    toolbar: CustomToolbar,
    event: (props) => <CustomEvent {...props} currentView={view} />
  }), [view]);
  
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        borderStyle: event.borderStyle,
        borderWidth: event.isHoliday ? '2px' : undefined,
        color: event.isHoliday ? 'white' : undefined,
        fontWeight: event.isHoliday ? 'bold' : undefined,
        opacity: event.isHoliday ? 0.9 : undefined,
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      className: event.isHoliday ? 'holiday-event' : undefined
    };
  };

  const dayPropGetter = (day) => {
    if (!isHolidayDate(day)) return {};
    return {
      className: 'holiday-day',
      style: {
        backgroundColor: 'rgba(138, 43, 226, 0.06)'
      }
    };
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Appointment Calendar
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/appointments/new')}
        >
          New Appointment
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="view-select-label">View</InputLabel>
              <Select
                labelId="view-select-label"
                id="view-select"
                value={view}
                label="View"
                onChange={(e) => handleViewChange(e.target.value)}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="agenda">Agenda</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              view={view}
              onView={handleViewChange}
              date={date}
              onNavigate={handleNavigate}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={dayPropGetter}
              popup
              tooltipAccessor={(event) => event.resource.tooltip || event.resource.description || event.resource.notes}
              components={components}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// Custom event component to enhance tooltip display
const CustomEvent = ({ event, currentView }) => {
  const isCompactView = currentView === 'day' || currentView === 'week';
  
  if (isCompactView) {
    // Single line format for Day and Week views
    // Remove newlines from title and use a compact format
    const singleLineTitle = event.title.replace(/\n/g, ', ');
    const personsCount = event.resource.numberOfPersons || 1;
    
    return (
      <div 
        title={event.resource.tooltip || event.title}
        style={{
          height: '100%',
          width: '100%',
          padding: '2px 4px',
          fontSize: '11px',
          lineHeight: '1.1',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis'
        }}
      >
        {!event.isHoliday 
           ? `${singleLineTitle} (P: ${personsCount})`
           : singleLineTitle
        }
      </div>
    );
  }

  return (
    <div 
      title={event.resource.tooltip || event.title}
      style={{
        height: '100%',
        width: '100%',
        padding: '2px 4px',
        fontSize: '12px',
        lineHeight: '1.2',
        overflow: 'hidden',
        whiteSpace: 'pre-line'
      }}
    >
      {event.title}
      {!event.isHoliday && (
        <div style={{ fontSize: '10px', marginTop: '2px' }}>
          Persons: {event.resource.numberOfPersons || 1}
        </div>
      )}
    </div>
  );
};

// Custom toolbar component to enhance the calendar's toolbar
const CustomToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.onNavigate('PREV');
  };
  
  const goToNext = () => {
    toolbar.onNavigate('NEXT');
  };
  
  const goToCurrent = () => {
    toolbar.onNavigate('TODAY');
  };
  
  const label = () => {
    const date = toolbar.date;
    // For day view, show both month and day
    if (toolbar.view === 'day') {
      return (
        <span>
          <strong>{format(date, 'MMMM d, yyyy')}</strong>
        </span>
      );
    }
    // For other views, show month and year
    return (
      <span>
        <strong>{format(date, 'MMMM yyyy')}</strong>
      </span>
    );
  };
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <Box>
        <Button onClick={goToCurrent}>Today</Button>
        <Button onClick={goToBack}>Back</Button>
        <Button onClick={goToNext}>Next</Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {label()}
      </Box>
      <Box>
        <Button onClick={() => toolbar.onView('month')}>Month</Button>
        <Button onClick={() => toolbar.onView('week')}>Week</Button>
        <Button onClick={() => toolbar.onView('day')}>Day</Button>
        <Button onClick={() => toolbar.onView('agenda')}>Agenda</Button>
      </Box>
    </Box>
  );
};

export default AppointmentCalendar;
